import { useLocation, useNavigate } from 'react-router-dom';
import { useEffect, useState, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';

/**
 * Flitship Mobile Bottom Navigation
 * 
 * Tab architecture:
 * 1. Accueil (/app) - Connected dashboard
 * 2. Colis (/colis) - Sender hub
 * 3. Trajets (/trajets) - Traveler hub
 * 4. Demandes (/requests) - Request decision hub
 * 5. Profil (/profile) - Identity & settings
 * 
 * Custom SVG Icons — Flitship Brand Universe
 * - Aviation, connection, corridors, trust themes
 */

const API = process.env.REACT_APP_BACKEND_URL;

// ============================================================
// CUSTOM SVG ICONS — Flitship Brand Universe
// ============================================================

// Icon 1: Accueil — House with center connection dot
const AccueilIcon = ({ isActive }) => (
  <svg 
    width="26" 
    height="26" 
    viewBox="0 0 28 28" 
    fill="none"
    strokeLinecap="round" 
    strokeLinejoin="round"
  >
    <path 
      d="M4 12L14 4L24 12V24H18V17H10V24H4V12Z" 
      stroke={isActive ? '#3c6df0' : '#737373'} 
      strokeWidth={isActive ? 2.5 : 2} 
      fill="none"
    />
    <circle 
      cx="14" 
      cy="15" 
      r="1.5" 
      fill={isActive ? '#3c6df0' : '#737373'}
    />
  </svg>
);

// Icon 2: Colis — 3D package/box
const ColisIcon = ({ isActive }) => (
  <svg 
    width="26" 
    height="26" 
    viewBox="0 0 28 28" 
    fill="none"
    strokeLinecap="round" 
    strokeLinejoin="round"
  >
    <path 
      d="M5 8L14 4L23 8V20L14 24L5 20V8Z" 
      stroke={isActive ? '#3c6df0' : '#737373'} 
      strokeWidth={isActive ? 2.5 : 2} 
      fill="none"
    />
    <path 
      d="M5 8L14 12L23 8" 
      stroke={isActive ? '#3c6df0' : '#737373'} 
      strokeWidth={isActive ? 2.5 : 2} 
      fill="none"
    />
    <path 
      d="M14 12V24" 
      stroke={isActive ? '#3c6df0' : '#737373'} 
      strokeWidth={isActive ? 2.5 : 2} 
      fill="none"
    />
  </svg>
);

// Icon 3: Trajets — Airplane with arc route
const TrajetsIcon = ({ isActive }) => (
  <svg 
    width="26" 
    height="26" 
    viewBox="0 0 28 28" 
    fill="none"
    strokeLinecap="round" 
    strokeLinejoin="round"
  >
    <path 
      d="M6 19L11 14M11 14L8 7L12 9L14 6L18 13M11 14L18 13M18 13L22 15L20 19L13 17" 
      stroke={isActive ? '#3c6df0' : '#737373'} 
      strokeWidth={isActive ? 2.5 : 2} 
      fill="none"
    />
  </svg>
);

// Icon 4: Demandes — Inbox with content lines
const DemandesIcon = ({ isActive }) => (
  <svg 
    width="26" 
    height="26" 
    viewBox="0 0 28 28" 
    fill="none"
    strokeLinecap="round" 
    strokeLinejoin="round"
  >
    <path 
      d="M5 6H23V18C23 19.1 22.1 20 21 20H7C5.9 20 5 19.1 5 18V6Z" 
      stroke={isActive ? '#3c6df0' : '#737373'} 
      strokeWidth={isActive ? 2.5 : 2} 
      fill="none"
    />
    <path 
      d="M5 13H10L12 16H16L18 13H23" 
      stroke={isActive ? '#3c6df0' : '#737373'} 
      strokeWidth={isActive ? 2.5 : 2} 
      fill="none"
    />
    <path 
      d="M9 10H19" 
      stroke={isActive ? '#3c6df0' : '#737373'} 
      strokeWidth={isActive ? 2.5 : 2} 
      fill="none"
    />
  </svg>
);

// Icon 5: Profil — User with trust shield badge
const ProfilIcon = ({ isActive }) => (
  <svg 
    width="26" 
    height="26" 
    viewBox="0 0 28 28" 
    fill="none"
    strokeLinecap="round" 
    strokeLinejoin="round"
  >
    {/* Head */}
    <circle 
      cx="14" 
      cy="9" 
      r="4" 
      stroke={isActive ? '#3c6df0' : '#737373'} 
      strokeWidth={isActive ? 2.5 : 2} 
      fill="none"
    />
    {/* Shoulders */}
    <path 
      d="M6 23C6 19.1 9.6 16 14 16C18.4 16 22 19.1 22 23" 
      stroke={isActive ? '#3c6df0' : '#737373'} 
      strokeWidth={isActive ? 2.5 : 2} 
      fill="none"
    />
    {/* Shield badge */}
    <path 
      d="M18 18L20 19.5V22C20 23.1 19.2 24 18.3 24.4L18 24.5L17.7 24.4C16.8 24 16 23.1 16 22V19.5L18 18Z" 
      stroke={isActive ? '#3c6df0' : '#737373'} 
      strokeWidth={1.5} 
      fill="none"
    />
  </svg>
);

// Icon 6: Messages — Chat bubble with lines
const MessagesIcon = ({ isActive }) => (
  <svg width="26" height="26" viewBox="0 0 28 28" fill="none"
    strokeLinecap="round" strokeLinejoin="round">
    <path
      d="M5 6H23C23.6 6 24 6.4 24 7V18C24 18.6 23.6 19 23 19H9L4 24V7C4 6.4 4.4 6 5 6Z"
      stroke={isActive ? '#3c6df0' : '#737373'}
      strokeWidth={isActive ? 2.5 : 2}
      fill="none"
    />
    <path d="M9 11H19"
      stroke={isActive ? '#3c6df0' : '#737373'}
      strokeWidth={isActive ? 2 : 1.5} fill="none" />
    <path d="M9 14.5H15"
      stroke={isActive ? '#3c6df0' : '#737373'}
      strokeWidth={isActive ? 2 : 1.5} fill="none" />
  </svg>
);

// ============================================================
// NAVIGATION CONFIG
// ============================================================

const NAV_ITEMS = [
  {
    id: 'accueil',
    label: 'Accueil',
    Icon: AccueilIcon,
    route: '/app'
  },
  {
    id: 'colis',
    label: 'Colis',
    Icon: ColisIcon,
    route: '/colis'
  },
  {
    id: 'trajets',
    label: 'Trajets',
    Icon: TrajetsIcon,
    route: '/trajets'
  },
  {
    id: 'messages',
    label: 'Messages',
    Icon: MessagesIcon,
    route: '/messages',
    hasBadge: true
  },
  {
    id: 'profil',
    label: 'Profil',
    Icon: ProfilIcon,
    route: '/profile'
  }
];

// ============================================================
// BOTTOM NAVIGATION COMPONENT
// ============================================================

export function BottomNavigation() {
  const location = useLocation();
  const navigate = useNavigate();
  const { getAccessToken } = useAuth();
  const currentPath = location.pathname;
  
  // Badge state for requests/notifications
  const [unreadCount, setUnreadCount] = useState(0);

  // Fetch unread notifications count
  const fetchUnreadCount = useCallback(async () => {
    const token = getAccessToken();
    if (!token) return;
    
    try {
      const response = await fetch(`${API}/api/notifications/unread-count`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setUnreadCount(data.count || 0);
      }
    } catch (err) {
      // Silently fail - badge is non-critical
    }
  }, [getAccessToken]);

  useEffect(() => {
    fetchUnreadCount();
    const interval = setInterval(fetchUnreadCount, 30000);
    return () => clearInterval(interval);
  }, [fetchUnreadCount]);

  // Determine active tab based on current path
  const getActiveTab = () => {
    // Exact matches first
    if (currentPath === '/app' || currentPath === '/dashboard') return 'accueil';
    if (currentPath === '/colis') return 'colis';
    if (currentPath === '/trajets') return 'trajets';
    if (currentPath === '/messages') return 'messages';
    if (currentPath === '/requests') return 'messages';
    if (currentPath === '/profile') return 'profil';
    
    // Prefix matches for nested routes
    if (currentPath.startsWith('/colis') || 
        currentPath.startsWith('/send-package') || 
        currentPath.startsWith('/create-shipment') || 
        currentPath.startsWith('/my-shipments')) {
      return 'colis';
    }
    if (currentPath.startsWith('/trajets') || 
        currentPath.startsWith('/my-trip') || 
        currentPath.startsWith('/publish-trip') || 
        currentPath.startsWith('/publish') ||
        currentPath.startsWith('/parcels')) {
      return 'trajets';
    }
    if (currentPath.startsWith('/notifications')) {
      return 'accueil';
    }
    if (currentPath.startsWith('/messages')) {
      return 'messages';
    }
    if (currentPath.startsWith('/profile') || 
        currentPath.startsWith('/identity') || 
        currentPath.startsWith('/verification') ||
        currentPath.startsWith('/hero-status')) {
      return 'profil';
    }
    if (currentPath.startsWith('/missions') || 
        currentPath.startsWith('/mission/')) {
      return 'accueil';
    }
    
    return 'accueil';
  };

  const activeTab = getActiveTab();

  return (
    <nav 
      className="
        fixed bottom-0 left-0 right-0 z-[9999]
        md:hidden
        bg-[#faf6f2]/95 backdrop-blur-sm
        border-t border-[#e8e4e0]
      "
      style={{
        paddingBottom: 'env(safe-area-inset-bottom, 0px)',
        height: 'calc(58px + env(safe-area-inset-bottom, 0px))'
      }}
      role="navigation"
      aria-label="Navigation principale"
      data-testid="bottom-navigation"
    >
      <div className="flex items-stretch justify-around h-full max-w-md mx-auto">
        {NAV_ITEMS.map((item) => {
          const isActive = activeTab === item.id;
          const Icon = item.Icon;
          const showBadge = item.hasBadge && unreadCount > 0;
          
          return (
            <button
              key={item.id}
              onClick={() => navigate(item.route)}
              className={`
                flex flex-col items-center justify-center
                flex-1 min-w-0
                py-1.5 px-1
                touch-manipulation
                active:scale-95
                transition-all duration-150
              `}
              aria-label={item.label}
              aria-current={isActive ? 'page' : undefined}
              data-testid={`nav-${item.id}`}
            >
              {/* Icon container */}
              <div className="relative flex items-center justify-center mb-1">
                <Icon isActive={isActive} />
                
                {/* Notification badge */}
                {showBadge && (
                  <span 
                    className="
                      absolute -top-1 -right-2
                      min-w-[16px] h-[16px] px-1
                      flex items-center justify-center
                      bg-[#f38a3e] text-white
                      text-[9px] font-bold
                      rounded-full
                      shadow-sm
                    "
                    aria-label={`${unreadCount} notification${unreadCount > 1 ? 's' : ''}`}
                  >
                    {unreadCount > 9 ? '9+' : unreadCount}
                  </span>
                )}
              </div>
              
              {/* Label with active pill */}
              {isActive ? (
                <span 
                  className="
                    bg-[#eef2fd] rounded-[6px] px-1.5 py-[1px]
                    text-[10px] leading-none tracking-wide
                    font-bold text-[#3c6df0]
                    transition-all duration-150
                  "
                >
                  {item.label}
                </span>
              ) : (
                <span 
                  className="
                    text-[10px] leading-none tracking-wide
                    font-semibold text-[#737373]
                    transition-colors duration-150
                  "
                >
                  {item.label}
                </span>
              )}
            </button>
          );
        })}
      </div>
    </nav>
  );
}

export default BottomNavigation;
